/**
 * CASA 2.4 — one allow-listed redirect target per app.
 *
 * Supabase Auth only accepts redirect URLs that appear verbatim in the project
 * allow-list. A URL we compute from a request origin (`window.location.origin`,
 * `VERCEL_URL`, a `*.vercel.app` preview host, `127.0.0.1` in the desktop shell)
 * is not on that list, so the OAuth / email / recovery hop either fails with
 * "requested path is invalid" or silently lands on the project Site URL instead
 * of the app — which is exactly the bounce users were seeing.
 *
 * So: every auth redirect is built from ONE per-app origin, and that origin is
 * only ever an allow-listed entry:
 *
 *   dashboard → https://dashboard.thekairos.app
 *   cms       → https://cms.thekairos.app
 *   admin     → https://admin.thekairos.app
 *
 * In production an off-domain `NEXT_PUBLIC_SITE_URL` (localhost, 127.0.0.1, a
 * `*.vercel.app` preview host, plain http) throws instead of quietly minting a
 * redirect target Supabase will reject.
 *
 * The env is injected by each caller: the Next apps read `process.env`, the Vite
 * admin SPA reads `import.meta.env`, so this module stays framework-agnostic.
 */

export type KairosAuthApp = 'dashboard' | 'cms' | 'admin'

/**
 * The allow-listed origin for each app. This table is the single source of
 * truth — do not derive these from the request or from `window`.
 */
export const APP_AUTH_ORIGINS = {
  dashboard: 'https://dashboard.thekairos.app',
  cms: 'https://cms.thekairos.app',
  admin: 'https://admin.thekairos.app',
} as const satisfies Record<KairosAuthApp, string>

/** Local dev servers, only ever reachable outside production. */
const DEV_ORIGINS: Record<KairosAuthApp, string> = {
  dashboard: 'http://localhost:3000',
  cms: 'http://localhost:3003',
  admin: 'http://localhost:5173',
}

/** Allow-listed paths. */
export const AUTH_CALLBACK_PATH = '/api/auth/callback'
export const ADMIN_OAUTH_CALLBACK_PATH = '/authentication/oauth-callback'
export const RESET_PASSWORD_PATH = '/authentication/reset-password'

/**
 * Native scheme entry on the allow-list. No mobile app ships from this repo, so
 * nothing builds it yet — the constant documents the allowed target and keeps a
 * future mobile client from inventing one.
 */
export const MOBILE_RESET_PASSWORD_URL = 'kairosconnect://reset-password'

export const DEFAULT_POST_VERIFICATION_PATH = '/dashboard'
export const DEFAULT_CMS_POST_LOGIN_PATH = '/cms'
export const DEFAULT_ADMIN_POST_LOGIN_PATH = '/admin'

/** First-party brand domains. Anything else is a misconfiguration. */
const FIRST_PARTY_ROOT_DOMAINS = ['thekairos.app', 'klarora.app', 'klarora.ai', 'klarora.nl', 'klarora.eu']

/** The public marketing site, which is never an app origin. */
const MARKETING_HOSTS = new Set([
  'thekairos.app',
  'www.thekairos.app',
  'klarora.app',
  'www.klarora.app',
  'klarora.ai',
  'klarora.nl',
  'klarora.eu',
])

export class AuthRedirectConfigError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'AuthRedirectConfigError'
  }
}

export interface AuthRedirectEnv {
  /**
   * Configured site URL: `NEXT_PUBLIC_SITE_URL` in the Next apps, the Vite
   * equivalent in admin. Deployment shares ONE value across every app
   * (`.github/workflows/cd.yml`), so its subdomain is never trusted — any
   * first-party host resolves onto the calling app's canonical origin.
   */
  readonly siteUrl?: string | undefined
  /** True only for a real production runtime. */
  readonly isProduction?: boolean | undefined
  /**
   * `window.location.origin`. Used only outside production (dev server, the
   * Tauri dev shell) so local flows keep working; never emitted in production.
   */
  readonly clientOrigin?: string | undefined
}

function normalizeHostname(hostname: string): string {
  return hostname.toLowerCase().replace(/^www\./, '')
}

/** Is this the public marketing site rather than an app origin? */
export function isMarketingHostname(hostname: string): boolean {
  return MARKETING_HOSTS.has(hostname.toLowerCase())
}

function isFirstPartyHostname(hostname: string): boolean {
  const host = hostname.toLowerCase()
  const bare = normalizeHostname(host)
  return FIRST_PARTY_ROOT_DOMAINS.some((root) => bare === root || bare.endsWith(`.${root}`))
}

function parseHttpUrl(value: string | undefined): URL | null {
  if (typeof value !== 'string' || value.trim() === '') return null
  try {
    const url = new URL(value.trim())
    return url.protocol === 'http:' || url.protocol === 'https:' ? url : null
  } catch {
    return null
  }
}

/**
 * Resolve the single site URL every auth redirect for `app` is built from.
 *
 * Production returns an allow-listed origin or throws; it can never return
 * `localhost`, `127.0.0.1`, `http://` or a `*.vercel.app` host.
 */
export function resolveAuthSiteUrl(app: KairosAuthApp, env: AuthRedirectEnv = {}): string {
  const canonical = APP_AUTH_ORIGINS[app]
  const configured = parseHttpUrl(env.siteUrl)

  if (!env.isProduction) {
    // Dev-only path. Rendering the desktop shell on 127.0.0.1 must keep its own
    // origin, and a local dev server must keep localhost.
    const origin = parseHttpUrl(env.clientOrigin) ?? configured
    if (!origin) return DEV_ORIGINS[app]
    if (isMarketingHostname(origin.hostname)) return canonical
    return origin.origin
  }

  if (configured) {
    if (configured.protocol !== 'https:' || !isFirstPartyHostname(configured.hostname)) {
      throw new AuthRedirectConfigError(
        `Refusing to build a ${app} auth redirect from an off-domain site URL ` +
          `(${configured.origin}). Set NEXT_PUBLIC_SITE_URL to a https://*.thekairos.app ` +
          `host or leave it unset; the app falls back to ${canonical}. ` +
          `Auth redirect targets must match the Supabase allow-list exactly.`,
      )
    }
  }

  // Either unset, or a first-party host (apex marketing domain, another app's
  // subdomain — the shared deploy secret does that). Emit this app's canonical
  // allow-listed origin in both cases.
  return canonical
}

export type AuthRedirectKind = 'auth-callback' | 'password-reset' | 'admin-oauth'

export interface BuildAuthRedirectOptions extends AuthRedirectEnv {
  /** Extra query parameters, e.g. `source=web`. */
  readonly params?: Readonly<Record<string, string>> | undefined
  /** Post-login destination for `admin-oauth`; same-origin paths only. */
  readonly next?: string | undefined
}

/**
 * Build an auth redirect target for `app`.
 *
 * - `auth-callback`  → `https://<app>.thekairos.app/api/auth/callback`
 * - `password-reset` → admin lands straight on the allow-listed
 *   `/authentication/reset-password`; dashboard and cms route through the
 *   allow-listed callback, because their recovery template needs the PKCE code
 *   exchange the callback route performs (`?next=` is admitted by the
 *   allow-list).
 * - `admin-oauth`    → `https://admin.thekairos.app/authentication/oauth-callback`
 */
export function buildAuthRedirectUrl(
  app: KairosAuthApp,
  kind: AuthRedirectKind,
  options: BuildAuthRedirectOptions = {},
): string {
  const siteUrl = resolveAuthSiteUrl(app, options)

  if (kind === 'admin-oauth') {
    const url = new URL(ADMIN_OAUTH_CALLBACK_PATH, siteUrl)
    url.searchParams.set('next', sanitizeNextPath(options.next, DEFAULT_ADMIN_POST_LOGIN_PATH))
    return url.toString()
  }

  if (kind === 'password-reset' && app === 'admin') {
    return new URL(RESET_PASSWORD_PATH, siteUrl).toString()
  }

  const url = new URL(AUTH_CALLBACK_PATH, siteUrl)
  if (kind === 'password-reset') {
    url.searchParams.set('next', RESET_PASSWORD_PATH)
    url.searchParams.set('type', 'recovery')
  }
  for (const [key, value] of Object.entries(options.params ?? {})) {
    url.searchParams.set(key, value)
  }
  return url.toString()
}

/**
 * Only same-origin, relative paths may be used as a post-auth destination.
 *
 * Rejected: `//evil.com`, `https://evil.com`, `/\evil.com`, control characters,
 * empty values, over-long values and anything not starting with `/`.
 */
export function sanitizeNextPath(
  raw: unknown,
  fallback: string = DEFAULT_POST_VERIFICATION_PATH,
): string {
  if (typeof raw !== 'string') return fallback
  const value = raw.trim()
  if (!value || value.length > 2048) return fallback
  if (!value.startsWith('/')) return fallback
  // Protocol-relative URLs (`//host`) and `\`-based variants are treated as
  // absolute by browsers, so `new URL(next, origin)` would leave our origin.
  if (value.startsWith('//')) return fallback
  if (value.includes('\\')) return fallback
  if (/[\u0000-\u001f\u007f]/.test(value)) return fallback
  return value
}
